/**
 * Sidebar — navegación principal lateral fija (240px).
 *
 * Doc 18 (brand) + alineado al prototipo de v1.
 *
 * Estructura de menú:
 *   - Items top-level: Home, Proyectos, Búsqueda(v2), Plantillas, Auditoría(v2).
 *   - Grupo "Configuración" (collapsible): items de /settings/*.
 *   - Grupo "Plataforma HIVA" (visible solo a platform admins):
 *     Clientes (/platform/clients) y Consumo IA (/platform/usage).
 *
 * Items con badge `v2` están grayed + cursor-not-allowed; preservan el
 * mental model del usuario sin habilitar funcionalidad. El badge es del
 * namespace `shell.nav.v2_badge`.
 *
 * Estado de los grupos persiste en localStorage para no exigir al user
 * re-expandirlo en cada visita.
 */

import { useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  type LucideIcon,
  Activity,
  BookOpen,
  Bell,
  Bot,
  Briefcase,
  Building2,
  ChevronDown,
  FolderKanban,
  LayoutDashboard,
  LayoutTemplate,
  Search,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react'

import type { MeResponse } from '../../api/me'
import {
  canAccessProjects,
  canReadTenantInternalResources,
  canUseAgentAccess,
  canViewAiUsage,
  isPlatformAdmin,
} from '../../lib/tiers'
import { BrandMark } from './BrandMark'
import { TenantSwitcher } from './TenantSwitcher'

interface SidebarProps {
  me: MeResponse | undefined
}

type NavLabelKey =
  | 'home'
  | 'projects'
  | 'search'
  | 'templates'
  | 'knowledge_base'
  | 'team'
  | 'usage'
  | 'notifications'
  | 'connected_agents'
  | 'platform'
  | 'clients'
  | 'audit'
  | 'settings'

interface NavItem {
  kind: 'item'
  to?: string
  /** True si la ruta está realmente implementada. False = grayed con badge v2. */
  enabled: boolean
  /** Match de path activo: usa startsWith para sub-rutas. */
  matchPrefix?: string
  labelKey: NavLabelKey
  icon: LucideIcon
  /** Si está set, el item solo aparece cuando el predicado es true. */
  visibleIf?: (me: MeResponse | undefined) => boolean
}

interface NavGroup {
  kind: 'group'
  labelKey: NavLabelKey
  icon: LucideIcon
  /** Slug usado como id para persistir collapsed state en localStorage. */
  storageKey: string
  /** Si está set, el grupo solo aparece cuando el predicado es true. */
  visibleIf?: (me: MeResponse | undefined) => boolean
  /** Items contenidos. El grupo aparece o no por su `visibleIf`; cada
   *  item, además, por el suyo. */
  children: Array<Omit<NavItem, 'kind'>>
}

type NavEntry = NavItem | NavGroup

/** Plantillas, Equipo y Base de conocimiento son recursos internos del
 *  tenant (OT-0015): el mismo predicado que gobierna sus rutas (`TierGate`)
 *  decide si aparecen en el menú. */
function canSeeTenantInternalResources(me: MeResponse | undefined): boolean {
  return canReadTenantInternalResources(me?.role)
}

const NAV_TREE: NavEntry[] = [
  {
    kind: 'item',
    to: '/',
    enabled: true,
    matchPrefix: '/',
    labelKey: 'home',
    icon: LayoutDashboard,
  },
  {
    kind: 'item',
    to: '/projects',
    enabled: true,
    matchPrefix: '/projects',
    labelKey: 'projects',
    icon: FolderKanban,
    visibleIf: (me) => canAccessProjects(me?.role),
  },
  { kind: 'item', enabled: false, labelKey: 'search', icon: Search },
  {
    kind: 'item',
    to: '/templates',
    enabled: true,
    matchPrefix: '/templates',
    labelKey: 'templates',
    icon: LayoutTemplate,
    visibleIf: canSeeTenantInternalResources,
  },
  {
    kind: 'group',
    labelKey: 'settings',
    icon: Settings,
    storageKey: 'settings',
    children: [
      {
        to: '/settings/knowledge-base',
        enabled: true,
        matchPrefix: '/settings/knowledge-base',
        labelKey: 'knowledge_base',
        icon: BookOpen,
        visibleIf: canSeeTenantInternalResources,
      },
      {
        to: '/settings/team',
        enabled: true,
        matchPrefix: '/settings/team',
        labelKey: 'team',
        icon: Users,
        visibleIf: canSeeTenantInternalResources,
      },
      {
        to: '/settings/usage',
        enabled: true,
        matchPrefix: '/settings/usage',
        labelKey: 'usage',
        icon: Activity,
        visibleIf: (me) => canViewAiUsage(me?.role),
      },
      {
        to: '/settings/notifications',
        enabled: true,
        matchPrefix: '/settings/notifications',
        labelKey: 'notifications',
        icon: Bell,
      },
      {
        to: '/settings/agents',
        enabled: true,
        matchPrefix: '/settings/agents',
        labelKey: 'connected_agents',
        icon: Bot,
        visibleIf: (me) => canUseAgentAccess(me?.role),
      },
    ],
  },
  { kind: 'item', enabled: false, labelKey: 'audit', icon: ShieldCheck },
  {
    kind: 'group',
    labelKey: 'platform',
    icon: Building2,
    storageKey: 'platform',
    visibleIf: isPlatformAdmin,
    children: [
      {
        to: '/platform/clients',
        enabled: true,
        matchPrefix: '/platform/clients',
        labelKey: 'clients',
        icon: Briefcase,
      },
      {
        to: '/platform/usage',
        enabled: true,
        matchPrefix: '/platform/usage',
        labelKey: 'usage',
        icon: Activity,
      },
    ],
  },
]

export function Sidebar({ me }: SidebarProps) {
  const { t } = useTranslation('shell')
  const { location } = useRouterState()
  const path = location.pathname

  return (
    <aside className="h-full w-60 shrink-0 border-r border-line bg-surface flex flex-col">
      <div className="px-5 py-4 border-b border-line flex items-center gap-2 shrink-0">
        <BrandMark className="h-7 w-7 text-brand" />
        <div className="font-display text-base">
          <span className="font-semibold">{t('brand.name')}</span>
          <span className="text-brand mx-0.5">·</span>
          <span className="text-ink-muted">{t('brand.product')}</span>
        </div>
      </div>

      <nav className="p-2 space-y-0.5 flex-1 min-h-0 overflow-y-auto">
        {NAV_TREE.filter(
          (entry) => !entry.visibleIf || entry.visibleIf(me),
        ).map((entry) =>
          entry.kind === 'item' ? (
            <NavRow key={entry.labelKey} item={entry} path={path} t={t} />
          ) : (
            <NavGroupRow
              key={entry.labelKey}
              group={entry}
              me={me}
              path={path}
              t={t}
            />
          ),
        )}
      </nav>

      <div className="border-t border-line p-3 shrink-0">
        <TenantSwitcher me={me} />
      </div>
    </aside>
  )
}

function NavRow({
  item,
  path,
  t,
  nested,
}: {
  item: NavItem | (Omit<NavItem, 'kind'> & { kind?: 'item' })
  path: string
  t: (key: string) => string
  nested?: boolean
}) {
  const Icon = item.icon
  const label = t(`nav.${item.labelKey}`)
  const padding = nested ? 'pl-9 pr-3' : 'px-3'

  if (!item.enabled) {
    return (
      <div
        className={`w-full flex items-center gap-3 ${padding} py-2 rounded-md text-sm text-ink-muted opacity-60 cursor-not-allowed`}
        aria-disabled="true"
        title={label}
      >
        {!nested ? <Icon className="w-4 h-4 shrink-0" /> : null}
        <span className="truncate">{label}</span>
        <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface-muted">
          {t('nav.v2_badge')}
        </span>
      </div>
    )
  }

  const isActive = item.matchPrefix
    ? item.matchPrefix === '/'
      ? path === '/'
      : path.startsWith(item.matchPrefix)
    : false

  return (
    <Link
      to={item.to!}
      className={
        isActive
          ? `w-full flex items-center gap-3 ${padding} py-2 rounded-md text-sm bg-brand-soft text-brand`
          : `w-full flex items-center gap-3 ${padding} py-2 rounded-md text-sm text-ink-muted hover:bg-surface-muted transition-colors`
      }
    >
      {!nested ? <Icon className="w-4 h-4 shrink-0" /> : null}
      <span className="truncate">{label}</span>
    </Link>
  )
}

function NavGroupRow({
  group,
  me,
  path,
  t,
}: {
  group: NavGroup
  me: MeResponse | undefined
  path: string
  t: (key: string) => string
}) {
  const Icon = group.icon
  const label = t(`nav.${group.labelKey}`)
  const children = group.children.filter((c) => !c.visibleIf || c.visibleIf(me))
  // Si algún child está activo, expandir por default (no respetar el
  // closed-state guardado en localStorage cuando hay match — sería
  // confuso ver el grupo cerrado con un item highlight invisible).
  const childActive = children.some((c) =>
    c.matchPrefix
      ? c.matchPrefix === '/'
        ? path === '/'
        : path.startsWith(c.matchPrefix)
      : false,
  )

  const storedKey = `sidebar.group.${group.storageKey}.open`
  const [open, setOpen] = useState<boolean>(() => {
    if (childActive) return true
    if (typeof window === 'undefined') return true
    const stored = window.localStorage.getItem(storedKey)
    if (stored === null) return true
    return stored === '1'
  })

  function toggle() {
    setOpen((prev) => {
      const next = !prev
      try {
        window.localStorage.setItem(storedKey, next ? '1' : '0')
      } catch {
        // localStorage puede fallar en modo incognito; aceptable.
      }
      return next
    })
  }

  return (
    <div className="space-y-0.5">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-ink-muted hover:bg-surface-muted transition-colors"
        aria-expanded={open}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="truncate">{label}</span>
        <ChevronDown
          className={`ml-auto w-3.5 h-3.5 transition-transform ${
            open ? 'rotate-0' : '-rotate-90'
          }`}
        />
      </button>
      {open ? (
        <div className="space-y-0.5">
          {children.map((child) => (
            <NavRow
              key={child.labelKey}
              item={child}
              path={path}
              t={t}
              nested
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
